import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { updateQualityStatus } from '@/services/procurementQualityStatusService';
import type { Procurement } from '@/types/database.types';
import { useQueryClient } from '@tanstack/react-query';
import type { GradeEntry } from '../GradeBreakdownInput';
import type { ItemQualityData } from '@/types/database/procurement-item-quality';

interface QualityInspectionData {
  sampleSizeChecked: boolean;
  sampleSizeNotes: string;
  damageKg: number;
  compensationKg: number;
  damagePercentage: number;
  damageNotes: string;
  qualityGrade: string;
  rejectionReason: string;
  gradeBreakdown: GradeEntry[];
  gradingType: 'preliminary' | 'final';
  finalGradingExpectedDate: string;
  preliminaryNotes: string;
}

export const useQualityInspectionActions = (
  procurement: Procurement | undefined,
  itemQualityData?: ItemQualityData[]
) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const isMultiItem = (procurement?.items?.length || 0) > 1;

  const handleApprove = async (inspectionData: QualityInspectionData) => {
    if (!procurement || !user) {
      toast({
        title: "Error",
        description: "Missing procurement or user information",
        variant: "destructive",
      });
      return;
    }

    if (!inspectionData.sampleSizeChecked) {
      toast({
        title: "Error",
        description: "Please verify the sample size before approving",
        variant: "destructive",
      });
      return;
    }

    // For single-item, require grade breakdown
    if (!isMultiItem && (!inspectionData.gradeBreakdown || inspectionData.gradeBreakdown.length === 0)) {
      toast({
        title: "Error",
        description: "Please add at least one grade breakdown entry",
        variant: "destructive",
      });
      return;
    }

    // For multi-item, validate item data
    if (isMultiItem && (!itemQualityData || itemQualityData.length === 0)) {
      toast({
        title: "Error",
        description: "Please complete quality inspection for all items",
        variant: "destructive",
      });
      return;
    }

    // Validate final grading expected date for preliminary assessments
    if (inspectionData.gradingType === 'preliminary' && !inspectionData.finalGradingExpectedDate) {
      toast({
        title: "Error",
        description: "Please provide the expected final grading date for preliminary assessments",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await updateQualityStatus({
        procurement,
        inspectionData: {
          sampleSizeChecked: inspectionData.sampleSizeChecked,
          sampleSizeNotes: inspectionData.sampleSizeNotes,
          damageKg: inspectionData.damageKg,
          compensationKg: inspectionData.compensationKg,
          damagePercentage: inspectionData.damagePercentage,
          damageNotes: inspectionData.damageNotes,
          qualityGrade: inspectionData.qualityGrade,
          gradeBreakdown: inspectionData.gradeBreakdown,
          gradingType: inspectionData.gradingType,
          finalGradingExpectedDate: inspectionData.finalGradingExpectedDate,
          preliminaryNotes: inspectionData.preliminaryNotes
        },
        itemQualityData: isMultiItem ? itemQualityData : undefined,
        userId: user.id,
        action: 'approve'
      });

      const successMessage = inspectionData.gradingType === 'preliminary'
        ? `Quality approved (preliminary). Payment can proceed based on sample assessment. Final grading expected by ${inspectionData.finalGradingExpectedDate}.`
        : `Quality approved. Procurement sent to warehouse for quantity verification.`;

      toast({
        title: "Success",
        description: successMessage,
      });

      await queryClient.invalidateQueries({ queryKey: ['procurements'] });
      navigate('/quality');
    } catch (err: any) {
      console.error("Quality approve error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to approve quality inspection",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (inspectionData: QualityInspectionData) => {
    if (!procurement || !user || !inspectionData.rejectionReason) {
      toast({
        title: "Error",
        description: "Please provide a rejection reason",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await updateQualityStatus({
        procurement,
        inspectionData: {
          sampleSizeChecked: inspectionData.sampleSizeChecked,
          sampleSizeNotes: inspectionData.sampleSizeNotes,
          damageKg: inspectionData.damageKg,
          compensationKg: inspectionData.compensationKg,
          damagePercentage: inspectionData.damagePercentage,
          damageNotes: inspectionData.damageNotes,
          qualityGrade: inspectionData.qualityGrade,
          rejectionReason: inspectionData.rejectionReason,
          gradeBreakdown: inspectionData.gradeBreakdown,
          gradingType: inspectionData.gradingType,
          finalGradingExpectedDate: inspectionData.finalGradingExpectedDate,
          preliminaryNotes: inspectionData.preliminaryNotes
        },
        itemQualityData: isMultiItem ? itemQualityData : undefined,
        userId: user.id,
        action: 'reject'
      });

      toast({
        title: "Success",
        description: "Procurement rejected due to quality issues.",
      });

      await queryClient.invalidateQueries({ queryKey: ['procurements'] });
      navigate('/quality');
    } catch (err: any) {
      console.error("Quality reject error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to reject procurement",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleApprove,
    handleReject,
    isSubmitting
  };
};
